// ThemeToggle (DESIGN §3.1.7): Segment „Dark · Light · System“ (Moon, Sun, Desktop), 32 im Menü,
// 40 in Einstellungen. Speichert sofort über useTheme. ARIA: radiogroup „Theme“ (Radix RadioGroup,
// ←/→ wechseln). Behelf für SegmentedControl aus steuerung (gleiche Optik). Mit Sitzungs-Override
// (`?theme=`) ist das wirksame Theme gewählt; eine Wahl hebt den Override auf (DESIGN §2.1).
import { Desktop, Moon, Sun } from '@phosphor-icons/react';
import { RadioGroup } from 'radix-ui';
import { cn } from '../kern/cn';
import { type ThemePreference, useTheme } from '../kern/theme';

const OPTIONS = [
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'system', label: 'System', icon: Desktop },
] as const;

export interface ThemeToggleProps {
  size?: 'sm' | 'md';
  /** Nur Icons (mit Namen für Screenreader). */
  iconsOnly?: boolean;
  className?: string;
  /** Nach der Wahl (z. B. Toast „Saved“ auf Einstellungsseiten). */
  onChange?: (preference: ThemePreference) => void;
}

export function ThemeToggle({ size = 'sm', iconsOnly, className, onChange }: ThemeToggleProps) {
  const { preference, theme, sessionOverride, setPreference } = useTheme();
  const selected: ThemePreference = sessionOverride ? theme : preference;
  return (
    <RadioGroup.Root
      aria-label="Theme"
      orientation="horizontal"
      value={selected}
      onValueChange={(value) => {
        setPreference(value as ThemePreference);
        onChange?.(value as ThemePreference);
      }}
      className={cn(
        'inline-flex shrink-0 items-center rounded-full bg-segment-track p-0.75',
        size === 'sm' ? 'h-8' : 'h-10',
        className,
      )}
    >
      {OPTIONS.map(({ value, label, icon: Glyph }) => (
        <RadioGroup.Item
          key={value}
          value={value}
          aria-label={label}
          title={iconsOnly ? label : undefined}
          className={cn(
            'inline-flex h-full items-center justify-center gap-1.5 rounded-full text-button text-fg-secondary transition-colors duration-(--bar-dur-fast) hover:text-fg data-[state=checked]:bg-segment-active data-[state=checked]:text-fg',
            iconsOnly ? 'aspect-square' : size === 'sm' ? 'px-2.5 text-label' : 'px-3.5',
          )}
        >
          <Glyph size={16} weight={selected === value ? 'fill' : 'regular'} aria-hidden />
          {iconsOnly ? null : <span>{label}</span>}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
}
